import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, MapPin, Archive, Trash, ArrowUUpLeft } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Sidebar } from "@/components/Sidebar";
import { PaymentBadge, ReportBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Dashboard() {
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    try {
      const { data } = await api.get("/dossiers");
      setDossiers(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const needsAction = (d) => !d.archived && (
    (d.payment_status === "paid" && d.report_status === "awaiting_report") ||
    d.report_status === "revision_requested" ||
    d.report_status === "validated" ||
    d.unread_message);

  const isTerminated = (d) => !d.archived && d.report_status === "definitive" && d.payment_status === "paid";

  const matchesSearch = (d) =>
    d.client_name.toLowerCase().includes(q.toLowerCase()) ||
    d.property_address.toLowerCase().includes(q.toLowerCase()) ||
    d.access_code.toLowerCase().includes(q.toLowerCase()) ||
    (d.payment_ref || "").toLowerCase().includes(q.toLowerCase());

  const filtered = dossiers.filter(matchesSearch).filter((d) => {
    if (filter === "archived") return d.archived;
    if (d.archived) return false;
    if (filter === "action") return needsAction(d);
    if (filter === "terminated") return isTerminated(d);
    return true;
  });

  const actionCount = dossiers.filter(needsAction).length;
  const terminatedCount = dossiers.filter(isTerminated).length;
  const archivedCount = dossiers.filter((d) => d.archived).length;
  const selectionMode = filter === "terminated" || filter === "archived";

  const changeFilter = (f) => { setFilter(f); setSelected(new Set()); };
  const toggleSel = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const archiveSelected = async () => {
    try {
      await Promise.all([...selected].map((id) => api.patch(`/dossiers/${id}/archived`, { archived: true })));
      toast.success(`${selected.size} dossier(s) archivé(s)`);
      setSelected(new Set());
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const unarchiveSelected = async () => {
    try {
      await Promise.all([...selected].map((id) => api.patch(`/dossiers/${id}/archived`, { archived: false })));
      toast.success(`${selected.size} dossier(s) restauré(s)`);
      setSelected(new Set());
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const deleteSelected = async () => {
    if (!window.confirm(`Supprimer définitivement ${selected.size} dossier(s) ? Cette action est irréversible.`)) return;
    try {
      await Promise.all([...selected].map((id) => api.delete(`/dossiers/${id}`)));
      toast.success(`${selected.size} dossier(s) supprimé(s)`);
      setSelected(new Set());
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const stats = {
    total: dossiers.filter((d) => !d.archived).length,
    pending: dossiers.filter((d) => !d.archived && d.payment_status === "pending").length,
    inReview: dossiers.filter((d) => !d.archived && ["provisional", "revision_requested", "validated"].includes(d.report_status)).length,
    done: dossiers.filter((d) => !d.archived && d.report_status === "definitive").length,
  };

  const btnCls = (active) =>
    `px-3.5 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1.5 ${active ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-50"}`;

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <main className="ml-64 p-8" data-testid="dashboard-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="label-eyebrow mb-1">Vue d'ensemble</p>
            <h1 className="font-heading text-3xl font-medium tracking-tight text-slate-950">Dossiers d'expertise</h1>
          </div>
          <Button data-testid="new-dossier-button" onClick={() => navigate("/admin/nouveau")}
            className="bg-blue-600 hover:bg-blue-700 h-11">
            <Plus size={18} weight="bold" className="mr-1.5" /> Nouveau dossier
          </Button>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total dossiers", value: stats.total, color: "text-slate-950" },
            { label: "Paiement en attente", value: stats.pending, color: "text-amber-600" },
            { label: "En révision", value: stats.inReview, color: "text-blue-600" },
            { label: "Rapports définitifs", value: stats.done, color: "text-emerald-600" },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-md p-6" data-testid={`stat-${s.label}`}>
              <p className="label-eyebrow mb-2">{s.label}</p>
              <p className={`font-heading text-4xl font-semibold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
          <div className="inline-flex rounded-md border border-slate-200 bg-white p-1 flex-wrap" data-testid="report-filter">
            <button data-testid="filter-all" onClick={() => changeFilter("all")} className={btnCls(filter === "all")}>
              Tous les rapports
            </button>
            <button data-testid="filter-action" onClick={() => changeFilter("action")} className={btnCls(filter === "action")}>
              Nécessitant mon intervention
              <span className={`px-1.5 py-0.5 rounded-full text-[11px] font-semibold ${filter === "action" ? "bg-white/25 text-white" : "bg-orange-100 text-orange-700"}`}>{actionCount}</span>
            </button>
            <button data-testid="filter-terminated" onClick={() => changeFilter("terminated")} className={btnCls(filter === "terminated")}>
              Terminés
              <span className={`px-1.5 py-0.5 rounded-full text-[11px] font-semibold ${filter === "terminated" ? "bg-white/25 text-white" : "bg-emerald-100 text-emerald-700"}`}>{terminatedCount}</span>
            </button>
            <button data-testid="filter-archived" onClick={() => changeFilter("archived")} className={btnCls(filter === "archived")}>
              Archivés
              <span className={`px-1.5 py-0.5 rounded-full text-[11px] font-semibold ${filter === "archived" ? "bg-white/25 text-white" : "bg-slate-100 text-slate-600"}`}>{archivedCount}</span>
            </button>
          </div>
          <div className="relative max-w-md flex-1 min-w-[220px]">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="search-input" placeholder="Rechercher client, adresse, code…"
              value={q} onChange={(e) => setQ(e.target.value)} className="pl-10 h-11 bg-white" />
          </div>
        </div>

        {selectionMode && selected.size > 0 && (
          <div className="flex items-center gap-3 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md" data-testid="bulk-bar">
            <span className="text-sm font-medium text-slate-700">{selected.size} sélectionné(s)</span>
            <div className="flex-1" />
            {filter === "terminated" ? (
              <Button data-testid="bulk-archive-button" size="sm" variant="outline" onClick={archiveSelected}>
                <Archive size={15} className="mr-1.5" /> Archiver la sélection
              </Button>
            ) : (
              <>
                <Button data-testid="bulk-unarchive-button" size="sm" variant="outline" onClick={unarchiveSelected}>
                  <ArrowUUpLeft size={15} className="mr-1.5" /> Restaurer
                </Button>
                <Button data-testid="bulk-delete-button" size="sm" variant="outline" onClick={deleteSelected}
                  className="text-red-600 border-red-200 hover:bg-red-50">
                  <Trash size={15} className="mr-1.5" /> Supprimer définitivement
                </Button>
              </>
            )}
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="dossiers-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left">
                {selectionMode && <th className="px-5 py-3 w-10"></th>}
                <th className="px-5 py-3 label-eyebrow font-semibold">Client</th>
                <th className="px-5 py-3 label-eyebrow font-semibold">Bien</th>
                <th className="px-5 py-3 label-eyebrow font-semibold">Paiement</th>
                <th className="px-5 py-3 label-eyebrow font-semibold">Rapport</th>
                <th className="px-5 py-3 label-eyebrow font-semibold text-right">Code</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={selectionMode ? 6 : 5} className="px-5 py-10 text-center text-slate-400">Chargement…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={selectionMode ? 6 : 5} className="px-5 py-10 text-center text-slate-400" data-testid="empty-state">
                  Aucun dossier.
                </td></tr>
              ) : (
                filtered.map((d) => (
                  <tr key={d.id} data-testid={`dossier-row-${d.id}`}
                    onClick={() => navigate(`/admin/dossier/${d.id}`)}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors">
                    {selectionMode && (
                      <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" data-testid={`select-${d.id}`} checked={selected.has(d.id)}
                          onChange={() => toggleSel(d.id)} className="w-4 h-4 accent-blue-600 cursor-pointer" />
                      </td>
                    )}
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <div>
                          <p className="font-medium text-slate-950">{d.client_name}</p>
                          <p className="text-xs text-slate-500">{d.client_email}</p>
                        </div>
                        {d.unread_message && (
                          <span data-testid={`unread-badge-${d.id}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-700 whitespace-nowrap">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Nouveau message
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5 text-slate-600">
                        <MapPin size={14} className="text-slate-400 shrink-0" />
                        <span className="truncate max-w-[200px]">{d.property_address}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4"><PaymentBadge status={d.payment_status} /></td>
                    <td className="px-5 py-4"><ReportBadge status={d.report_status} paid={d.payment_status === "paid"} /></td>
                    <td className="px-5 py-4 text-right font-mono text-xs text-slate-500">{d.access_code}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
